// Main authenticated dashboard at root path (/)
// Note: Previously located at /signed-in, now at root. All path references verified and up to date.
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useFindMany, useUser, useGlobalAction, useFindFirst } from "@gadgetinc/react";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, SettingsIcon } from "@chakra-ui/icons";
import { api } from "../api";
import { normalizeRoles, hasAnyRole } from '../lib/roleUtils';
import { devLog, devLogData } from '../lib/devUtils';
import AttendanceCheckIn from "../components/AttendanceCheckIn";

export default function SignedIn() {
  const user = useUser(api);
  
  const navigate = useNavigate();
  const [{ data: assistants, fetching, error }] = useFindMany(api.assistant);
  const [{ data: accessCheckData }, checkAccess] = useGlobalAction(api.checkAssistantAccess);
  const [{ data: agentAccessData }, checkAgentAccess] = useGlobalAction(api.checkAgentAccess);
  const [{ data: agentConfigsData }, getAgentConfigurations] = useGlobalAction(api.getAgentConfigurations);
  const [{ data: publicAccessData }, checkPublicAccess] = useGlobalAction(api.checkPublicScenarioAccess);
  const [{ data: accessProfile }, getCurrentUserAccessProfile] =
    useGlobalAction(api.getCurrentUserAccessProfile);
  const [{ data: instructorRollingCodeData, fetching: instructorRollingCodeFetching }, getInstructorRollingShareCode] =
    useGlobalAction(api.getInstructorRollingShareCode);
  const [availableAssistants, setAvailableAssistants] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [rollingCodeSecondsRemaining, setRollingCodeSecondsRemaining] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    getCurrentUserAccessProfile({});
  }, [user?.id]);

  // Fetch institutional configuration
  const [{ data: institutionConfig, fetching: configFetching, error: configError }] = useFindFirst(api.institutionConfiguration, {
    filter: { institution: { equals: user?.institution || "elevation_ems" } },
    select: {
      id: true,
      institution: true,
      enableFisdapShiftProcessing: true,
      enableShiftScheduling: true,
      enablePatientEncounterTracking: true,
      enablePreceptorEvaluations: true,
      enableCalendarIntegration: true,
      enableEducationalContentLibrary: true,
      enableClinicalQuickNotes: true,
      enablePreceptorScoring: true,
      cohortManagementEnabled: true,
      accessCodeSystemEnabled: true,
      requirePreceptorApproval: true,
      documentRetentionPolicyDays: true,
      allowedDocumentTypes: true,
      defaultShiftTypes: true,
      evaluationFormTypes: true,
      brandingSettings: true,
      workflowSettings: true
    }
  });

  // Fetch user's cohort memberships to check for instructor role and FISDAP review access
  const [{ data: userMemberships }] = useFindMany(api.cohortMembership, {
    filter: { 
      userId: { equals: user?.id },
      active: { equals: true }
    },
    select: {
      primaryRole: true,
      active: true,
      hasFisdapReviewAccess: true,
      hasScenarioGeneratorAccess: true,
      cohort: {
        cohortScenarioGeneratorAccess: true,
        grantScenarioAccessToInstructors: true
      }
    },
    pause: !user?.id
  });

  // Today's shifts query
  const todayRange = useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(); end.setHours(23, 59, 59, 999);
    return { start, end };
  }, []);

  const [{ data: todayShifts, fetching: shiftsFetching }, refetchShifts] = useFindMany(api.shiftDocumentation, {
    filter: {
      studentId: { equals: user?.id },
      shift: {
        shiftDate: { greaterThanOrEqual: todayRange.start, lessThanOrEqual: todayRange.end },
        status: { notEquals: "cancelled" },
      },
    },
    select: {
      id: true,
      checkInAt: true, checkOutAt: true,
      checkInWithinGeofence: true, checkOutWithinGeofence: true,
      checkInDistanceMeters: true, checkOutDistanceMeters: true,
      enrollmentStatus: true, attendanceVerified: true,
      shift: {
        id: true, shiftDate: true, endTime: true, shiftType: true,
        location: true, unit: true, status: true,
        clinicalSite: { id: true, name: true },
      },
    },
    pause: !user?.id,
  });

  const handleShiftUpdate = useCallback(() => {
    refetchShifts();
  }, [refetchShifts]);

  const attendanceActionRange = useMemo(() => {
    const now = new Date();
    return {
      actionStart: new Date(now.getTime() - 4 * 60 * 60 * 1000),
      actionEnd: new Date(now.getTime() + 24 * 60 * 60 * 1000),
    };
  }, []);

  const [{ data: attendanceActionDocs, fetching: attendanceActionFetching }, refetchAttendanceActionDocs] = useFindMany(api.shiftDocumentation, {
    filter: {
      studentId: { equals: user?.id },
      shift: {
        shiftDate: { lessThanOrEqual: attendanceActionRange.actionEnd },
        endTime: { greaterThanOrEqual: attendanceActionRange.actionStart },
        status: { notEquals: "cancelled" },
      },
    },
    select: {
      id: true,
      checkInAt: true,
      checkOutAt: true,
      checkInWithinGeofence: true,
      checkOutWithinGeofence: true,
      checkInDistanceMeters: true,
      checkOutDistanceMeters: true,
      shift: {
        id: true,
        shiftDate: true,
        endTime: true,
        shiftType: true,
        location: true,
        unit: true,
        status: true,
        clinicalSite: { id: true, name: true },
      },
    },
    pause: !user?.id,
  });

  const handleAttendanceActionUpdate = useCallback(() => {
    refetchShifts();
    refetchAttendanceActionDocs();
  }, [refetchAttendanceActionDocs, refetchShifts]);

  // Upcoming shifts (next 7 days, excluding today)
  const upcomingRange = useMemo(() => {
    const start = new Date(); start.setDate(start.getDate() + 1); start.setHours(0, 0, 0, 0);
    const end = new Date(); end.setDate(end.getDate() + 7); end.setHours(23, 59, 59, 999);
    return { start, end };
  }, []);

  const [{ data: upcomingShifts }] = useFindMany(api.shiftDocumentation, {
    filter: {
      studentId: { equals: user?.id },
      shift: {
        shiftDate: { greaterThanOrEqual: upcomingRange.start, lessThanOrEqual: upcomingRange.end },
        status: { notEquals: "cancelled" },
      },
    },
    select: {
      id: true,
      completionStatus: true,
      formChecklist: true,
      shift: {
        id: true, shiftDate: true, endTime: true, shiftType: true,
        location: true, status: true,
        clinicalSite: { id: true, name: true },
      },
    },
    sort: { shift: { shiftDate: "Ascending" } },
    first: 5,
    pause: !user?.id,
  });

  // Outstanding docs: past shifts with incomplete documentation
  const [{ data: outstandingDocs }] = useFindMany(api.shiftDocumentation, {
    filter: {
      studentId: { equals: user?.id },
      completionStatus: { notEquals: "complete" },
      shift: {
        shiftDate: { lessThan: todayRange.start },
        status: { notEquals: "cancelled" },
      },
    },
    select: {
      id: true,
      completionStatus: true,
      formChecklist: true,
      shift: {
        id: true, shiftDate: true, shiftType: true,
        location: true, status: true,
        clinicalSite: { id: true, name: true },
      },
    },
    sort: { shift: { shiftDate: "Descending" } },
    first: 5,
    pause: !user?.id,
  });

  // Canonical gadget-level role and code-level role resolution from access profile.
  // Fallback to session roles so admin UI doesn't disappear if access profile is delayed/unavailable.
  const roleSource = user?.roles || user?.roleList || [];
  const hasAdminRole =
    accessProfile?.roles?.isGadgetAdmin === true ||
    hasAnyRole(roleSource, ["admin", "system-admin", "sysadmin"]);
  // Retained variable name to avoid touching the entire dashboard tree right now.
  const hasSystemAdminRole = hasAdminRole;

  // Check for FISDAP review access
  // Priority: gadget admin > cohortMembership (instructor role or hasFisdapReviewAccess flag)
  // FISDAP review access is controlled through:
  // 1. Gadget admin role - always has access
  // 2. Cohort-based instructor roles - users with active cohortMembership where primaryRole='instructor'
  // 3. Explicit cohortMembership permission - hasFisdapReviewAccess flag for fine-grained control per cohort
  // 4. Fallback to memberships while profile is loading
  const hasFisdapReviewAccess =
    accessProfile?.clinicalDocs?.canReviewFisdap === true ||
    userMemberships?.some((m) => m.primaryRole === "instructor") ||
    userMemberships?.some((m) => m.hasFisdapReviewAccess === true) ||
    false;

  const hasEffectiveCohortScenarioAccess =
    userMemberships?.some((membership) =>
      membership?.active === true &&
      (
        membership?.hasScenarioGeneratorAccess === true ||
        (
          membership?.primaryRole === "instructor" &&
          membership?.cohort?.cohortScenarioGeneratorAccess === true &&
          membership?.cohort?.grantScenarioAccessToInstructors === true
        )
      )
    ) ?? false;

  // Check for Scenario Generator access
  // Priority: admin roles > access profile > effective cohort membership fallback
  const hasScenarioGeneratorAccess =
    hasAdminRole ||
    accessProfile?.scenario?.hasAccess === true ||
    hasEffectiveCohortScenarioAccess;

  const hasInstructorRole =
    accessProfile?.roles?.isInstructor === true ||
    (userMemberships?.some((m) => m?.primaryRole === "instructor") ?? false);

  const actionableAttendanceDocs = useMemo(() => {
    const now = new Date();
    const canCheckInDoc = (doc) => {
      const start = doc?.shift?.shiftDate ? new Date(doc.shift.shiftDate) : null;
      const end = doc?.shift?.endTime ? new Date(doc.shift.endTime) : null;
      const open = start ? new Date(start.getTime() - 60 * 60 * 1000) : null;
      return !!(!doc?.checkInAt && start && end && now >= open && now <= end && doc?.shift?.shiftType !== "lab");
    };
    const canCheckOutDoc = (doc) => {
      const end = doc?.shift?.endTime ? new Date(doc.shift.endTime) : null;
      const open = end ? new Date(end.getTime() - 60 * 60 * 1000) : null;
      const close = end ? new Date(end.getTime() + 4 * 60 * 60 * 1000) : null;
      return !!(doc?.checkInAt && !doc?.checkOutAt && end && now >= open && now <= close && doc?.shift?.shiftType !== "lab");
    };

    return (attendanceActionDocs || []).filter((doc) => canCheckInDoc(doc) || canCheckOutDoc(doc));
  }, [attendanceActionDocs]);

  const loadInstructorRollingCode = useCallback(async () => {
    if (!user?.id || !hasInstructorRole) return;
    try {
      await getInstructorRollingShareCode({});
    } catch (error) {
      console.error("Failed to load instructor rolling share code", error);
    }
  }, [user?.id, hasInstructorRole, getInstructorRollingShareCode]);

  // Check assistant access when user loads
  useEffect(() => {
    if (user?.email) {
      checkAccess({ userEmail: user.email });
    }
  }, [user?.email, checkAccess]);

  // Check agent access when user loads
  useEffect(() => {
    if (user?.id) {
      devLog('Checking agent access for user');
      checkAgentAccess({ userId: user.id });
    }
  }, [user?.id, checkAgentAccess]);

  // Fetch agent configurations when component loads
  useEffect(() => {
    getAgentConfigurations();
  }, [getAgentConfigurations]);

  // Check public scenario access when component loads
  useEffect(() => {
    checkPublicAccess();
  }, [checkPublicAccess]);

  useEffect(() => {
    if (!user?.id || !hasInstructorRole) return;
    loadInstructorRollingCode();
  }, [user?.id, hasInstructorRole, loadInstructorRollingCode]);

  useEffect(() => {
    if (!instructorRollingCodeData?.validUntil) {
      setRollingCodeSecondsRemaining(null);
      return;
    }

    const tick = () => {
      const ms = new Date(instructorRollingCodeData.validUntil).getTime() - Date.now();
      setRollingCodeSecondsRemaining(Math.max(0, Math.floor(ms / 1000)));
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [instructorRollingCodeData?.validUntil]);

  // Filter assistants based on access permissions
  useEffect(() => {
    if (accessCheckData?.availableAssistantIds && assistants) {
      const filtered = assistants.filter(assistant => 
        accessCheckData.availableAssistantIds.includes(assistant.id) &&
        assistant.assistantType !== 'document_processor' &&
        // Hide admin assistants from the main interface
        assistant.name !== 'Amy' &&
        !assistant.name?.toLowerCase().includes('administrative') &&
        !assistant.name?.toLowerCase().includes('admin assistant')
      );
      setAvailableAssistants(filtered);
    } else {
      setAvailableAssistants([]);
    }
  }, [accessCheckData, assistants]);

  // Process available agents based on access permissions and real configurations
  useEffect(() => {
    devLog('Agent access data updated');
    devLog('Agent configurations data received');
    devLogData('Agent configurations structure', agentConfigsData);
    
    if (agentAccessData?.hasAccess && agentConfigsData?.data) {
      devLog('User has agent access, processing real agent configurations');
      devLog('Processing accessible agents');
      devLog('Processing raw configurations data');
      
      try {
        // Icon and theme mapping for different agent types
        const agentStyling = {
          'don': { icon: '🧂', theme: '#3b82f6' },
          'maia': { icon: '👩‍⚕️', theme: '#8b5cf6' },
          'gerald': { icon: '🩺', theme: '#10b981' },
          'glenn': { icon: '📋', theme: '#f59e0b' }
        };
        
        // Check if data is an array or if we need to access a property
        const configurations = Array.isArray(agentConfigsData.data) 
          ? agentConfigsData.data 
          : agentConfigsData.data.configurations || [];
          
        devLog('Processed configurations count:', configurations?.length || 0);
        
        // Filter configurations to only include accessible agents
        const accessibleAgentTypes = agentAccessData.accessibleAgents || [];
        const filteredConfigs = configurations
          .filter(config => config.isActive && accessibleAgentTypes.includes(config.name))
          .map(config => ({
            type: config.name,
            name: config.name,
            title: config.description || config.name,
            description: config.description || 'AI agent ready to assist you.',
            instructions: config.instructions,
            capabilities: config.capabilities,
            icon: agentStyling[config.name.toLowerCase()]?.icon || '🤖',
            theme: agentStyling[config.name.toLowerCase()]?.theme || '#6b7280'
          }));

        setAvailableAgents(filteredConfigs);
        devLog('Available agents set with real configurations:', filteredConfigs.length);
      } catch (error) {
        devLog('Error processing agent configurations:', error?.message || 'Unknown error');
        setAvailableAgents([]);
      }
    } else {
      devLog('No agent access or configurations not loaded');
      devLog('Has access:', !!agentAccessData?.hasAccess);
      devLog('Has configurations:', !!agentConfigsData?.data);
      if (agentConfigsData?.error) {
        devLog('Configurations error occurred');
      }
      setAvailableAgents([]);
    }
  }, [agentAccessData, agentConfigsData]);

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (featureName) => {
    if (hasSystemAdminRole) return true; // Gadget admin sees everything
    return institutionConfig?.[featureName] || false;
  };

  const handleStartChat = (assistantId) => {
    navigate(`/chat/${assistantId}`);
  };

  const handleStartAgent = (agentName) => {
    navigate(`/agents/${agentName}`);
  };



  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#111111",
      color: "#ffffff",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      padding: "24px 32px",
      borderBottom: "1px solid #2d2d2d",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "24px",
    },
    logo: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#3b82f6",
      margin: 0,
    },
    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: "24px",
      flexWrap: "wrap",
    },
    navigation: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    navLink: {
      color: "#3b82f6",
      textDecoration: "none",
      fontSize: "16px",
      fontWeight: "500",
      padding: "8px 16px",
      borderRadius: "6px",
      transition: "all 0.2s ease",
      border: "1px solid #3b82f6",
    },
    userInfo: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    welcomeText: {
      fontSize: "16px",
      color: "#d1d5db",
    },
    roleText: {
      fontSize: "14px",
      color: "#9ca3af",
      fontStyle: "italic",
    },
    signOutButton: {
      backgroundColor: "#374151",
      color: "#ffffff",
      border: "1px solid #4b5563",
      padding: "8px 16px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    main: {
      padding: "40px 32px",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    title: {
      fontSize: "24px",
      fontWeight: "600",
      marginBottom: "8px",
      color: "#ffffff",
    },
    subtitle: {
      fontSize: "16px",
      color: "#9ca3af",
      marginBottom: "32px",
    },
    loading: {
      textAlign: "center",
      padding: "60px 20px",
      fontSize: "16px",
      color: "#9ca3af",
    },
    error: {
      textAlign: "center",
      padding: "60px 20px",
      fontSize: "16px",
      color: "#ef4444",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
    },
    emptyStateTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#ffffff",
      marginBottom: "8px",
    },
    emptyStateText: {
      fontSize: "16px",
      color: "#9ca3af",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "24px",
    },
    card: {
      backgroundColor: "#1e1e1e",
      border: "1px solid #2d2d2d",
      borderRadius: "12px",
      padding: "24px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
    },
    cardTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#3b82f6",
      marginBottom: "12px",
      margin: 0,
    },
    cardDescription: {
      fontSize: "14px",
      color: "#d1d5db",
      lineHeight: "1.6",
      marginBottom: "20px",
    },
    cardAction: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "14px",
      fontWeight: "500",
      color: "#3b82f6",
    },
    separator: {
      width: "100%",
      height: "1px",
      backgroundColor: "#2d2d2d",
      margin: "48px 0",
    },
    sectionTitle: {
      fontSize: "24px",
      fontWeight: "600",
      marginBottom: "8px",
      color: "#ffffff",
    },
    sectionSubtitle: {
      fontSize: "16px",
      color: "#9ca3af",
      marginBottom: "32px",
    },
    betaBadge: {
      backgroundColor: "#f59e0b",
      color: "#000000",
      fontSize: "12px",
      fontWeight: "700",
      padding: "4px 8px",
      borderRadius: "4px",
      marginLeft: "8px",
    },
    agentCard: {
      backgroundColor: "#1e1e1e",
      border: "1px solid #2d2d2d",
      borderRadius: "12px",
      padding: "24px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
    },
    agentIcon: {
      fontSize: "32px",
      marginBottom: "12px",
      display: "block",
    },
    rollingCodeBanner: {
      background: "linear-gradient(90deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
      border: "1px solid #475569",
      borderRadius: "12px",
      padding: "18px 20px",
      marginBottom: "24px",
    },
    rollingCodeValue: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: "32px",
      fontWeight: "800",
      letterSpacing: "0.22em",
      color: "#f8fafc",
      margin: "8px 0 6px",
    },
    rollingCodeMeta: {
      fontSize: "13px",
      color: "#cbd5e1",
    },
  };

  if (user === undefined) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading user data...</div>
      </div>
    );
  }

  if (user === null) {
    // User is explicitly null (no session), show redirecting message
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Redirecting...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.logo}>ElevationEMS AI</h1>
        <div style={styles.headerRight}>
          <nav style={styles.navigation}>
            {/* Admin-only links */}
            {hasAdminRole && (
              <>
                <a 
                  href="/admin"
                  style={styles.navLink}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#3b82f6";
                    e.currentTarget.style.color = "#ffffff";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#3b82f6";
                  }}
                >
                  Admin Dashboard
                </a>
                
                <a 
                  href="/admin/ai-system-generator"
                  style={{
                    ...styles.navLink,
                    backgroundColor: "#8b5cf6",
                    borderColor: "#8b5cf6",
                    color: "#ffffff",
                    fontWeight: "600",
                    position: "relative"
                  }}
                  title="Create custom AI data analysis systems for your institution"
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#7c3aed";
                    e.currentTarget.style.borderColor = "#7c3aed";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 92, 246, 0.3)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "#8b5cf6";
                    e.currentTarget.style.borderColor = "#8b5cf6";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  🧠 AI System Generator
                </a>

                <a
                  href="/eval-systems"
                  style={{
                    ...styles.navLink,
                    backgroundColor: "#f59e0b",
                    borderColor: "#f59e0b",
                    color: "#ffffff",
                    fontWeight: "600",
                    position: "relative"
                  }}
                  title="Build and manage document evaluation systems"
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#d97706";
                    e.currentTarget.style.borderColor = "#d97706";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(245, 158, 11, 0.3)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "#f59e0b";
                    e.currentTarget.style.borderColor = "#f59e0b";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  Eval Systems
                </a>

                {/* Medical Control link - System Admin only */}
                {hasSystemAdminRole && (
                  <a 
                    href="/medical-control"
                    style={{
                      ...styles.navLink,
                      backgroundColor: "#ef4444",
                      borderColor: "#ef4444",
                      color: "#ffffff",
                      fontWeight: "600",
                      position: "relative"
                    }}
                    title="Practice calling online medical control for clinical guidance"
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = "#dc2626";
                      e.currentTarget.style.borderColor = "#dc2626";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(239, 68, 68, 0.3)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = "#ef4444";
                      e.currentTarget.style.borderColor = "#ef4444";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    📞 Medical Control
                  </a>
                )}
              </>
            )}

          </nav>
          <div style={styles.userInfo}>
            <div>
              <div style={styles.welcomeText}>
                Welcome, {user.firstName || user.email}
              </div>
              <div style={styles.roleText}>
                Roles: {normalizeRoles(user.roles || []).join(', ') || 'None'}
              </div>
            </div>
            <a
              href="/sign-out"
              style={{
                ...styles.signOutButton,
                textDecoration: 'none',
                display: 'inline-block',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#4b5563";
                e.currentTarget.style.borderColor = "#6b7280";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#374151";
                e.currentTarget.style.borderColor = "#4b5563";
              }}
            >
              Sign Out
            </a>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        {!attendanceActionFetching && actionableAttendanceDocs.length > 0 && (
          <div style={{
            background: "linear-gradient(135deg, rgba(14,165,233,0.14), rgba(16,185,129,0.12))",
            border: "1px solid rgba(56,189,248,0.35)",
            borderRadius: "16px",
            padding: "22px",
            marginBottom: "24px",
            boxShadow: "0 12px 28px rgba(0,0,0,0.22)",
          }}>
            <div style={{ fontSize: "12px", fontWeight: 800, color: "#7dd3fc", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "8px" }}>
              Action Needed
            </div>
            <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Shift Attendance</h2>
            <p style={{ ...styles.sectionSubtitle, marginBottom: "18px" }}>
              Attendance buttons are live for the shift windows that need action right now.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {actionableAttendanceDocs.map((doc) => {
                const shift = doc.shift;
                const shiftStart = shift?.shiftDate ? new Date(shift.shiftDate) : null;
                const shiftEnd = shift?.endTime ? new Date(shift.endTime) : null;
                return (
                  <div
                    key={`action-${doc.id}`}
                    style={{
                      backgroundColor: "rgba(15,23,42,0.82)",
                      border: "1px solid rgba(71,85,105,0.65)",
                      borderRadius: "12px",
                      padding: "18px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: "220px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "17px", fontWeight: "700", color: "#ffffff" }}>
                            {shift?.clinicalSite?.name || shift?.location || "Unknown Location"}
                          </span>
                          {shift?.shiftType && (
                            <span style={{
                              backgroundColor: shift.shiftType === "field" ? "#10b981" : shift.shiftType === "hospital" ? "#3b82f6" : shift.shiftType === "lab" ? "#f59e0b" : "#6b7280",
                              color: "#ffffff",
                              fontSize: "11px",
                              fontWeight: "700",
                              padding: "3px 9px",
                              borderRadius: "999px",
                              textTransform: "uppercase",
                            }}>
                              {shift.shiftType}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "14px", color: "#cbd5e1" }}>
                          {shiftStart ? shiftStart.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                          {" — "}
                          {shiftEnd ? shiftEnd.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                          {shift?.unit ? ` | ${shift.unit}` : ""}
                        </div>
                      </div>
                      <div style={{ minWidth: "300px" }}>
                        <AttendanceCheckIn
                          shiftDocId={doc.id}
                          shiftType={shift?.shiftType}
                          shiftStart={shift?.shiftDate}
                          shiftEnd={shift?.endTime}
                          locationName={shift?.clinicalSite?.name || shift?.location || "Unknown Location"}
                          checkInAt={doc.checkInAt}
                          checkOutAt={doc.checkOutAt}
                          checkInWithinGeofence={doc.checkInWithinGeofence}
                          checkOutWithinGeofence={doc.checkOutWithinGeofence}
                          checkInDistanceMeters={doc.checkInDistanceMeters}
                          checkOutDistanceMeters={doc.checkOutDistanceMeters}
                          onUpdate={handleAttendanceActionUpdate}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {hasInstructorRole && (
          <div style={styles.rollingCodeBanner}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
              <div>
                <div style={{ fontSize: "13px", fontWeight: 700, color: "#93c5fd", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Evaluator Rolling Access Code
                </div>
                <div style={styles.rollingCodeValue}>
                  {instructorRollingCodeData?.code
                    ? `${String(instructorRollingCodeData.code).slice(0, 4)} ${String(instructorRollingCodeData.code).slice(4, 8)}`
                    : (instructorRollingCodeFetching ? "Loading..." : "--------")}
                </div>
                <div style={styles.rollingCodeMeta}>
                  {instructorRollingCodeData?.validUntil
                    ? `Expires in ${String(Math.floor((rollingCodeSecondsRemaining || 0) / 60)).padStart(2, "0")}:${String((rollingCodeSecondsRemaining || 0) % 60).padStart(2, "0")} (${new Date(instructorRollingCodeData.validUntil).toLocaleString()})`
                    : "No active evaluator code. Generate one when you are ready to start."}
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => {
                    getInstructorRollingShareCode({ generate: true });
                  }}
                  style={{
                    backgroundColor: "#1d4ed8",
                    color: "#ffffff",
                    border: "1px solid #1e40af",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Generate Code
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (instructorRollingCodeData?.code) {
                      navigator.clipboard.writeText(String(instructorRollingCodeData.code)).catch(() => {});
                    }
                  }}
                  style={{
                    backgroundColor: "#0ea5e9",
                    color: "#ffffff",
                    border: "1px solid #0284c7",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Copy
                </button>
                <a
                  href="/instructor-access-codes"
                  style={{
                    color: "#e2e8f0",
                    border: "1px solid #64748b",
                    textDecoration: "none",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                >
                  Open Code Page
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Today's Shifts Section */}
        {!shiftsFetching && todayShifts && todayShifts.length > 0 && (
          <>
            <h2 style={styles.sectionTitle}>Today's Shifts</h2>
            <p style={styles.sectionSubtitle}>
              Your scheduled shifts for today
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "0" }}>
              {todayShifts.map((doc) => {
                const shift = doc.shift;
                const shiftStart = shift?.shiftDate ? new Date(shift.shiftDate) : null;
                const shiftEnd = shift?.endTime ? new Date(shift.endTime) : null;
                return (
                  <div
                    key={doc.id}
                    style={{
                      backgroundColor: "#1e1e1e",
                      border: "1px solid #2d2d2d",
                      borderRadius: "12px",
                      padding: "20px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: "200px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "16px", fontWeight: "600", color: "#ffffff" }}>
                            {shift?.clinicalSite?.name || shift?.location || "Unknown Location"}
                          </span>
                          {shift?.shiftType && (
                            <span style={{
                              backgroundColor: shift.shiftType === "field" ? "#10b981" : shift.shiftType === "hospital" ? "#3b82f6" : shift.shiftType === "lab" ? "#f59e0b" : "#6b7280",
                              color: "#ffffff",
                              fontSize: "11px",
                              fontWeight: "600",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              textTransform: "uppercase",
                            }}>
                              {shift.shiftType}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "14px", color: "#9ca3af" }}>
                          {shiftStart ? shiftStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—"}
                          {" — "}
                          {shiftEnd ? shiftEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—"}
                          {shift?.unit ? ` | ${shift.unit}` : ""}
                        </div>
                      </div>
                      <div style={{ minWidth: "280px" }}>
                        <AttendanceCheckIn
                          shiftDocId={doc.id}
                          shiftType={shift?.shiftType}
                          shiftStart={shift?.shiftDate}
                          shiftEnd={shift?.endTime}
                          locationName={shift?.clinicalSite?.name || shift?.location || "Unknown Location"}
                          checkInAt={doc.checkInAt}
                          checkOutAt={doc.checkOutAt}
                          checkInWithinGeofence={doc.checkInWithinGeofence}
                          checkOutWithinGeofence={doc.checkOutWithinGeofence}
                          checkInDistanceMeters={doc.checkInDistanceMeters}
                          checkOutDistanceMeters={doc.checkOutDistanceMeters}
                          onUpdate={handleAttendanceActionUpdate}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={styles.separator}></div>
          </>
        )}

        {/* Outstanding Documents Alert */}
        {outstandingDocs && outstandingDocs.length > 0 && (
          <>
            <div style={{
              backgroundColor: "#7f1d1d",
              border: "1px solid #dc2626",
              borderRadius: "12px",
              padding: "16px 20px",
              marginBottom: "24px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <span style={{ fontSize: "16px", fontWeight: "600", color: "#fca5a5" }}>
                  Outstanding Shift Documents ({outstandingDocs.length})
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {outstandingDocs.map((doc) => {
                  const shift = doc.shift;
                  const shiftDate = shift?.shiftDate ? new Date(shift.shiftDate) : null;
                  const checklist = Array.isArray(doc.formChecklist) ? doc.formChecklist : [];
                  const incomplete = checklist.filter(f => !f.completed).length;
                  return (
                    <div
                      key={doc.id}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        backgroundColor: "#991b1b", padding: "8px 12px", borderRadius: "6px", cursor: "pointer",
                      }}
                      onClick={() => navigate(`/shift-details/${shift?.id}`)}
                    >
                      <div>
                        <span style={{ color: "#ffffff", fontSize: "14px", fontWeight: "500" }}>
                          {shift?.clinicalSite?.name || shift?.location || "Shift"}
                        </span>
                        <span style={{ color: "#fca5a5", fontSize: "13px", marginLeft: "8px" }}>
                          {shiftDate ? shiftDate.toLocaleDateString() : ""}
                        </span>
                      </div>
                      <div>
                        <span style={{
                          backgroundColor: "#dc2626", color: "#fff", fontSize: "11px",
                          fontWeight: "600", padding: "2px 8px", borderRadius: "4px",
                        }}>
                          {incomplete > 0 ? `${incomplete} form${incomplete > 1 ? 's' : ''} due` : doc.completionStatus}
                        </span>
                        <span style={{ color: "#fca5a5", fontSize: "13px", marginLeft: "8px" }}>→</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Upcoming Shifts */}
        {upcomingShifts && upcomingShifts.length > 0 && (
          <>
            <h2 style={{ ...styles.sectionTitle, fontSize: "18px" }}>Upcoming Shifts</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "0" }}>
              {upcomingShifts.map((doc) => {
                const shift = doc.shift;
                const shiftDate = shift?.shiftDate ? new Date(shift.shiftDate) : null;
                return (
                  <div
                    key={doc.id}
                    style={{
                      backgroundColor: "#1e1e1e", border: "1px solid #2d2d2d",
                      borderRadius: "8px", padding: "12px 16px",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(`/shift-details/${shift?.id}`)}
                  >
                    <div>
                      <span style={{ color: "#ffffff", fontSize: "14px", fontWeight: "500" }}>
                        {shift?.clinicalSite?.name || shift?.location || "Shift"}
                      </span>
                      {shift?.shiftType && (
                        <span style={{
                          backgroundColor: shift.shiftType === "field" ? "#10b981" : shift.shiftType === "hospital" ? "#3b82f6" : "#6b7280",
                          color: "#fff", fontSize: "10px", fontWeight: "600",
                          padding: "1px 6px", borderRadius: "3px", marginLeft: "8px", textTransform: "uppercase",
                        }}>
                          {shift.shiftType}
                        </span>
                      )}
                    </div>
                    <div style={{ color: "#9ca3af", fontSize: "13px" }}>
                      {shiftDate ? shiftDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "—"}
                      <span style={{ marginLeft: "8px", color: "#6b7280" }}>→</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={styles.separator}></div>
          </>
        )}

        {(fetching || error || (availableAssistants && availableAssistants.length > 0)) && (
          <>
            <h2 style={styles.sectionTitle}>AI Assistants</h2>
            <p style={styles.sectionSubtitle}>
              Choose an assistant to start a conversation
            </p>

            {fetching && (
              <div style={styles.loading}>Loading assistants...</div>
            )}

            {error && (
              <div style={styles.error}>
                Error loading assistants: {error.toString()}
              </div>
            )}

            {!fetching && !error && (!availableAssistants || availableAssistants.length === 0) && (
              <div style={styles.emptyState}>
                <h3 style={styles.emptyStateTitle}>No Assistants Available</h3>
                <p style={styles.emptyStateText}>
                  {assistants && assistants.length > 0 
                    ? "You don't have access to any AI assistants at this time. Please contact your administrator for access."
                    : "There are no AI assistants available at this time."
                  }
                </p>
              </div>
            )}

            {!fetching && !error && availableAssistants && availableAssistants.length > 0 && (
              <div style={styles.grid}>
                {availableAssistants.map((assistant) => (
                  <div
                    key={assistant.id}
                    style={styles.card}
                    onClick={() => handleStartChat(assistant.id)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = "#252525";
                      e.currentTarget.style.borderColor = "#3b82f6";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.1)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = "#1e1e1e";
                      e.currentTarget.style.borderColor = "#2d2d2d";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <h3 style={styles.cardTitle}>
                      {assistant.name || "AI Assistant"}
                    </h3>
                    <p style={styles.cardDescription}>
                      {assistant.description || "Ready to assist you with your questions and tasks."}
                    </p>
                    <div style={styles.cardAction}>
                      <span>Start Chat</span>
                      <span>→</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}



        {availableAgents && availableAgents.length > 0 && (
          <>
            <div style={styles.separator}></div>

            <h2 style={styles.sectionTitle}>
              AI Agents
              <span style={styles.betaBadge}>BETA!!!</span>
            </h2>
            <p style={styles.sectionSubtitle}>
              Next-generation AI agents with specialized capabilities
            </p>

            <div style={styles.grid}>
              {availableAgents.map((agent) => (
                <div
                  key={agent.name}
                  style={{
                    ...styles.agentCard,
                    borderColor: agent.theme + '40',
                  }}
                  onClick={() => handleStartAgent(agent.name)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#252525";
                    e.currentTarget.style.borderColor = agent.theme;
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = `0 8px 24px ${agent.theme}20`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "#1e1e1e";
                    e.currentTarget.style.borderColor = agent.theme + '40';
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div style={styles.agentIcon}>{agent.icon}</div>
                  <h3 style={{...styles.cardTitle, color: agent.theme}}>
                    {agent.name}
                  </h3>
                  <p style={styles.cardDescription}>
                    {agent.description}
                  </p>
                  <div style={{...styles.cardAction, color: agent.theme}}>
                    <span>Start Session</span>
                    <span>→</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {(fetching || error || (availableAssistants && availableAssistants.length > 0) || (availableAgents && availableAgents.length > 0)) && (
          <div style={styles.separator}></div>
        )}

        <h2 style={styles.sectionTitle}>Tools</h2>
        <p style={styles.sectionSubtitle}>
          Additional tools and services
        </p>

        <div style={styles.grid}>
          {configFetching && (
            <div style={styles.loading}>Loading configuration...</div>
          )}

          {configError && (
            <div style={styles.error}>
              Error loading institution configuration. Some features may not be available.
            </div>
          )}

          {hasAdminRole && isFeatureEnabled('hasDocumentProcessing') && (
            <div
              style={styles.card}
              onClick={() => navigate('/document-processing')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={styles.cardTitle}>Document Analysis</h3>
              <p style={styles.cardDescription}>
                Upload and analyze clinical documents with AI assistance. Get comprehensive analysis and insights from your medical documents.
              </p>
              <div style={styles.cardAction}>
                <span>Process Documents</span>
                <span>→</span>
              </div>
            </div>
          )}
          
          {hasAdminRole && isFeatureEnabled('hasPatientEncounterTracking') && (
            <div
              style={styles.card}
              onClick={() => navigate('/patient-encounters')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={styles.cardTitle}>Patient Encounter Form</h3>
              <p style={styles.cardDescription}>
                Create and manage patient encounter documentation. Generate comprehensive encounter forms with AI assistance for medical record keeping.
              </p>
              <div style={styles.cardAction}>
                <span>Create Encounter</span>
                <span>→</span>
              </div>
            </div>
          )}
          
          {isFeatureEnabled('hasShiftScheduling') && (
            <div
              style={styles.card}
              onClick={() => navigate('/shifts')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={styles.cardTitle}>Shift Dashboard</h3>
              <p style={styles.cardDescription}>
                Manage your clinical shifts and patient encounters. Track shift schedules, log patient interactions, and maintain comprehensive clinical documentation.
              </p>
              <div style={styles.cardAction}>
                <span>Manage Shifts</span>
                <span>→</span>
              </div>
            </div>
          )}
          
          {isFeatureEnabled('hasCalendarIntegration') && (
            <div
              style={styles.card}
              onClick={() => navigate('/calendar')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={{...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px'}}>
                <CalendarIcon />
                Calendar
              </h3>
              <p style={styles.cardDescription}>
                View shift calendar and schedule. Track your clinical rotations, shift assignments, and important dates in an organized calendar view.
              </p>
              <div style={styles.cardAction}>
                <span>View Calendar</span>
                <span>→</span>
              </div>
            </div>
          )}
          
          {isFeatureEnabled('hasEducationalContentLibrary') && (
            <div
              style={styles.card}
              onClick={() => navigate('/educational-content')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={{...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px'}}>
                📚
                Educational Content Library
              </h3>
              <p style={styles.cardDescription}>
                Browse practice questions, clinical scenarios, and study materials. Access comprehensive educational resources to enhance your medical knowledge and skills.
              </p>
              <div style={styles.cardAction}>
                <span>Browse Content</span>
                <span>→</span>
              </div>
            </div>
          )}

          {(hasInstructorRole || hasSystemAdminRole) && (
            <div
              style={styles.card}
              onClick={() => navigate('/question-bank')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#a855f7";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(168, 85, 247, 0.15)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={{...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px', color: '#a855f7'}}>
                🧪
                Question Bank
              </h3>
              <p style={styles.cardDescription}>
                Create and curate exam items with AI chat. Filter by level, topic, difficulty, and question type, then publish approved questions to the public instructor pool.
              </p>
              <div style={{...styles.cardAction, color: '#a855f7'}}>
                <span>Open Question Bank</span>
                <span>→</span>
              </div>
            </div>
          )}
          
          {publicAccessData?.hasAccess && (
            <div
              style={styles.card}
              onClick={() => navigate('/public-scenarios')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(59, 130, 246, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={{...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px'}}>
                📖
                Public Scenarios
              </h3>
              <p style={styles.cardDescription}>
                Browse community-contributed clinical scenarios. Access a library of shared training scenarios from educators and institutions.
              </p>
              <div style={styles.cardAction}>
                <span>Browse Scenarios</span>
                <span>→</span>
              </div>
            </div>
          )}
          
          {isFeatureEnabled('enableFisdapShiftProcessing') && (
            <div
              style={styles.card}
              onClick={() => navigate('/clindocs-submissions')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#10b981";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(16, 185, 129, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={{...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981'}}>
                📋
                ClinDocs Submissions
              </h3>
              <p style={styles.cardDescription}>
                Track your clinical document submissions and processing status. View AI evaluations, instructor feedback, and request reviews for your shift documentation.
              </p>
              <div style={{...styles.cardAction, color: '#10b981'}}>
                <span>View Submissions</span>
                <span>→</span>
              </div>
            </div>
          )}

          {hasFisdapReviewAccess && (
            <div
              style={styles.card}
              onClick={() => navigate('/fisdap-review')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#10b981";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(16, 185, 129, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={{...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981'}}>
                📋
                FISDAP Review
              </h3>
              <p style={styles.cardDescription}>
                Review and evaluate clinical documentation submissions, resolve HITL items, and manage shift-level feedback.
              </p>
              <div style={{...styles.cardAction, color: '#10b981'}}>
                <span>Open FISDAP Review</span>
                <span>→</span>
              </div>
            </div>
          )}

          {hasFisdapReviewAccess && (
            <div
              style={styles.card}
              onClick={() => navigate('/fisdap-reports')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#22c55e";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(34, 197, 94, 0.12)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={{...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px', color: '#22c55e'}}>
                📊
                FISDAP Reports
              </h3>
              <p style={styles.cardDescription}>
                View cohort and student-level performance summaries with filtered shift counts, statuses, and shift detail drill-down.
              </p>
              <div style={{...styles.cardAction, color: '#22c55e'}}>
                <span>Open FISDAP Reports</span>
                <span>→</span>
              </div>
            </div>
          )}

          {hasScenarioGeneratorAccess && (
            <div
              style={styles.card}
              onClick={() => navigate('/my-scenarios')}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#252525";
                e.currentTarget.style.borderColor = "#8b5cf6";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(139, 92, 246, 0.12)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1e1e1e";
                e.currentTarget.style.borderColor = "#2d2d2d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <h3 style={{...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px', color: '#8b5cf6'}}>
                🩺
                Scenario Generator
              </h3>
              <p style={styles.cardDescription}>
                Generate and manage AI-powered training scenarios for cohorts, including voice sim, evaluator shares, and scenario library workflows.
              </p>
              <div style={{...styles.cardAction, color: '#8b5cf6'}}>
                <span>Open Scenario Generator</span>
                <span>→</span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
